import { Home, Search, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOS } from '@/lib/os-context';
import { motion } from 'framer-motion';
import { liquidButton } from '@/lib/liquid-motion';

interface MobileNavBarProps {
  onSearchClick?: () => void;
  onHomeClick?: () => void;
}

export function MobileNavBar({ onSearchClick, onHomeClick }: MobileNavBarProps) {
  const { openWindow, effectiveColorMode, displaySettings } = useOS();
  const isLight = effectiveColorMode === 'light';
  const isTahoe = displaySettings.theme === 'tahoe';

  return (
    <motion.div 
      className={cn(
        "fixed flex items-center justify-around px-4 z-[9999] shadow-[0_-8px_28px_rgba(15,23,42,0.18)]",
        isTahoe
          ? "tahoe-mobile-nav left-4 right-4 bottom-3 h-16 border pointer-events-auto"
          : "bottom-0 left-0 right-0 h-14 border-t",
        !isTahoe && isLight 
          ? "bg-white/95 backdrop-blur-2xl border-gray-200/90" 
          : !isTahoe && "bg-gray-950/95 backdrop-blur-2xl border-white/10"
      )}
      initial={{ y: isTahoe ? 72 : 56 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      {/* Home Button */}
      <motion.button
        onClick={onHomeClick}
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 py-2 px-6 rounded-xl transition-colors",
          isLight ? "active:bg-gray-100" : "active:bg-gray-800"
        )}
        whileTap={{ scale: 0.9 }}
        whileHover={isTahoe ? liquidButton.hover : undefined}
        aria-label="Home"
      >
        <Home 
          size={24} 
          className={cn(
            isLight ? "text-gray-800" : "text-gray-200"
          )} 
        />
        <span className={cn(
          "text-[10px] font-semibold",
          isLight ? "text-gray-800" : "text-gray-200"
        )}>
          Home
        </span>
      </motion.button>

      {/* Search Button */}
      <motion.button
        onClick={onSearchClick}
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 py-2 px-6 rounded-xl transition-colors",
          isLight ? "active:bg-gray-100" : "active:bg-gray-800"
        )}
        whileTap={{ scale: 0.9 }}
        whileHover={isTahoe ? liquidButton.hover : undefined}
        aria-label="Search"
      >
        <Search 
          size={24} 
          className={cn(
            isLight ? "text-gray-800" : "text-gray-200"
          )} 
        />
        <span className={cn(
          "text-[10px] font-semibold",
          isLight ? "text-gray-800" : "text-gray-200"
        )}>
          Search
        </span>
      </motion.button>

      {/* Profile Button */}
      <motion.button
        onClick={() => openWindow('profile')}
        className={cn(
          "flex flex-col items-center justify-center gap-0.5 py-2 px-6 rounded-xl transition-colors",
          isLight ? "active:bg-gray-100" : "active:bg-gray-800"
        )}
        whileTap={{ scale: 0.9 }}
        whileHover={isTahoe ? liquidButton.hover : undefined}
        aria-label="Profile"
      >
        <User 
          size={24} 
          className={cn(
            isLight ? "text-gray-800" : "text-gray-200"
          )} 
        />
        <span className={cn(
          "text-[10px] font-semibold",
          isLight ? "text-gray-800" : "text-gray-200"
        )}>
          Profile
        </span>
      </motion.button>
    </motion.div>
  );
}
